import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { FetchParams } from './employeesSlice';
import { buildQueryString } from '../../utils/queryStringBuilder';
import { 
  recordAssignment, 
  recordStatusChange, 
  recordPause, 
  recordResume, 
  recordCompletion 
} from './timeEntriesSlice';

export interface Assignment {
  id: number;
  attributes: {
    status: 'pending' | 'in_progress' | 'paused' | 'completed';
    assignedAt: string;
    startedAt: string | null;
    completedAt: string | null;
    pausedAt: string | null;
    pauseReason: string | null;
    notes: string;
    isAutoAssigned: boolean;
    dress: {
      data: {
        id: number;
        attributes: {
          Name: string;
          Type: string;
          project: {
            data: {
              id: number;
              attributes: {
                name: string;
                client: {
                  data: {
                    id: number;
                    attributes: {
                      firstName: string;
                      lastName: string;
                    }
                  }
                }
              }
            }
          }
        }
      }
    };
    stage: {
      data: {
        id: number;
        attributes: {
          name: string;
          order: number;
          requiredRole: string;
        }
      }
    };
    employee: {
      data: {
        id: number;
        attributes: {
          Name: string;
          lastName: string;
          role: string;
        }
      } | null;
    };
    assignedBy: {
      data: {
        id: number;
        attributes: {
          username: string;
        }
      }
    };
  }
}

interface AssignmentsState {
  assignments: Assignment[];
  employeeAssignments: Assignment[];
  currentAssignment: Assignment | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: AssignmentsState = {
  assignments: [],
  employeeAssignments: [],
  currentAssignment: null,
  isLoading: false,
  error: null
};

export const fetchAssignments = createAsyncThunk(
  'assignments/fetchAll',
  async (params: FetchParams = {}, { rejectWithValue }) => {
    try {
      const queryString = buildQueryString(params);
      const response = await api.get(`/api/assignments${queryString}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch assignments');
    }
  }
);

export const fetchAssignment = createAsyncThunk(
  'assignments/fetchOne',
  async (id: number, { rejectWithValue }) => {
    try {
      const queryString = buildQueryString({ populate: '*' });
      const response = await api.get(`/api/assignments/${id}${queryString}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch assignment');
    }
  }
);

export const fetchEmployeeAssignments = createAsyncThunk(
  'assignments/fetchByEmployee',
  async (employeeId: number, { rejectWithValue }) => {
    try {
      const queryString = buildQueryString({
        populate: '*',
        filters: {
          employee: {
            id: {
              $eq: employeeId
            }
          }
        }
      });
      const response = await api.get(`/api/assignments${queryString}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch employee assignments');
    }
  }
);

export const fetchDressAssignments = createAsyncThunk(
  'assignments/fetchByDress',
  async ({ dressId, projectId }: { dressId: number; projectId?: number }, { rejectWithValue }) => {
    try {
      const filters: any = {
        dress: {
          id: {
            $eq: dressId
          }
        }
      };
      
      // Add project filter if provided
      if (projectId) {
        filters.dress.project = {
          id: {
            $eq: projectId
          }
        };
      }
      
      const queryString = buildQueryString({
        populate: '*',
        filters
      });
      
      const response = await api.get(`/api/assignments${queryString}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch dress assignments');
    }
  }
);

export const fetchProjectAssignments = createAsyncThunk(
  'assignments/fetchByProject',
  async (projectId: number, { rejectWithValue }) => {
    try {
      const queryString = buildQueryString({
        populate: '*',
        filters: {
          dress: {
            project: {
              id: {
                $eq: projectId
              }
            }
          }
        }
      });
      const response = await api.get(`/api/assignments${queryString}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch project assignments');
    }
  }
);

export const createAssignment = createAsyncThunk(
  'assignments/create',
  async (assignmentData: any, { rejectWithValue, dispatch }) => {
    try {
      // Always set status to pending for new assignments
      const data = {
        ...assignmentData,
        status: 'pending'
      };
      const response = await api.post('/api/assignments', { data });
      
      // Record time entry for assignment creation
      if (response.data?.data?.id) {
        const assignment = response.data.data;
        const employeeId = assignmentData.employee;
        const stageId = assignmentData.stage;
        const dressId = assignmentData.dress;
        const projectId = assignmentData.project || assignment.attributes?.dress?.data?.attributes?.project?.data?.id;
        
        dispatch(recordAssignment(
          assignment.id,
          employeeId,
          stageId,
          dressId,
          projectId || 0,
          `Assignment created for ${assignment.attributes?.dress?.data?.attributes?.Name || 'dress'}`
        ));
      }
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to create assignment');
    }
  }
);

export const updateAssignmentStatus = createAsyncThunk(
  'assignments/updateStatus',
  async ({ id, status, reason }: { id: number; status: string; reason?: string }, { rejectWithValue, dispatch, getState }) => {
    try {
      const updateData: any = { status };
      
      // Add appropriate timestamp based on status
      if (status === 'in_progress') {
        updateData.startedAt = new Date().toISOString();
        updateData.pausedAt = null;
        updateData.pauseReason = null;
      } else if (status === 'paused') {
        updateData.pausedAt = new Date().toISOString();
        updateData.pauseReason = reason || '';
      } else if (status === 'completed') {
        updateData.completedAt = new Date().toISOString();
        updateData.pausedAt = null;
        updateData.pauseReason = null;
      }
      
      const response = await api.put(`/api/assignments/${id}`, {
        data: updateData
      });
      
      // Get the full assignment data for time entry recording
      const assignment = response.data.data;
      const employeeId = assignment.attributes?.employee?.data?.id;
      const stageId = assignment.attributes?.stage?.data?.id;
      const dressId = assignment.attributes?.dress?.data?.id;
      const projectId = assignment.attributes?.dress?.data?.attributes?.project?.data?.id;
      
      if (employeeId && stageId && dressId) {
        // Record the appropriate time entry based on status
        if (status === 'in_progress') {
          dispatch(recordResume(id, employeeId, stageId, dressId, projectId || 0));
        } else if (status === 'paused') {
          dispatch(recordPause(id, employeeId, stageId, dressId, projectId || 0, reason || 'No reason provided'));
        } else if (status === 'completed') {
          dispatch(recordCompletion(id, employeeId, stageId, dressId, projectId || 0));
        } else {
          dispatch(recordStatusChange(id, employeeId, stageId, dressId, projectId || 0, status));
        }
      }
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to update assignment status');
    }
  }
);

export const updateAssignment = createAsyncThunk(
  'assignments/update',
  async ({ id, assignmentData }: { id: number; assignmentData: any }, { rejectWithValue }) => {
    try {
      // If stage is being changed, always set status to pending
      const data = {
        ...assignmentData,
        status: 'pending'
      };
      const response = await api.put(`/api/assignments/${id}`, {
        data
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to update assignment');
    }
  }
);

export const autoAssignEmployee = createAsyncThunk(
  'assignments/autoAssign',
  async ({ stageId, dressId, projectId }: { stageId: number; dressId: number; projectId?: number }, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/assignments/auto-assign', {
        data: {
          stageId,
          dressId,
          projectId,
          status: 'pending'
        }
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Failed to auto-assign employee');
    }
  }
);

const assignmentsSlice = createSlice({
  name: 'assignments',
  initialState,
  reducers: {
    clearAssignmentError(state) {
      state.error = null;
    },
    clearCurrentAssignment(state) {
      state.currentAssignment = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all assignments
      .addCase(fetchAssignments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAssignments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.assignments = action.payload.data;
      })
      .addCase(fetchAssignments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch single assignment
      .addCase(fetchAssignment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAssignment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentAssignment = action.payload.data;
      })
      .addCase(fetchAssignment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch employee assignments
      .addCase(fetchEmployeeAssignments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeAssignments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.employeeAssignments = action.payload.data;
      })
      .addCase(fetchEmployeeAssignments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Create assignment
      .addCase(createAssignment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAssignment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.assignments.push(action.payload.data);
      })
      .addCase(createAssignment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update assignment status
      .addCase(updateAssignmentStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAssignmentStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedAssignment = action.payload.data;
        
        // Update in assignments array
        const index = state.assignments.findIndex(a => a.id === updatedAssignment.id);
        if (index !== -1) {
          state.assignments[index] = updatedAssignment;
        }
        
        // Update in employeeAssignments array
        const empIndex = state.employeeAssignments.findIndex(a => a.id === updatedAssignment.id);
        if (empIndex !== -1) {
          state.employeeAssignments[empIndex] = updatedAssignment;
        }
        
        // Update current assignment if it's the same
        if (state.currentAssignment && state.currentAssignment.id === updatedAssignment.id) {
          state.currentAssignment = updatedAssignment;
        }
      })
      .addCase(updateAssignmentStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update assignment
      .addCase(updateAssignment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAssignment.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedAssignment = action.payload.data;
        
        // Update in assignments array
        const index = state.assignments.findIndex(a => a.id === updatedAssignment.id);
        if (index !== -1) {
          state.assignments[index] = updatedAssignment;
        }
        
        // Update in employeeAssignments array
        const empIndex = state.employeeAssignments.findIndex(a => a.id === updatedAssignment.id);
        if (empIndex !== -1) {
          state.employeeAssignments[empIndex] = updatedAssignment;
        }
        
        // Update current assignment if it's the same
        if (state.currentAssignment && state.currentAssignment.id === updatedAssignment.id) {
          state.currentAssignment = updatedAssignment;
        }
      })
      .addCase(updateAssignment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch dress assignments
      .addCase(fetchDressAssignments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDressAssignments.fulfilled, (state, action) => {
        state.isLoading = false;
        // Don't overwrite all assignments, this endpoint is for specific dresses
        // Just update or append the new assignments to the existing array
        const newAssignments = action.payload.data;
        if (newAssignments && Array.isArray(newAssignments)) {
          // For each new assignment, check if it exists in the current state
          newAssignments.forEach(newAssignment => {
            const existingIndex = state.assignments.findIndex(a => a.id === newAssignment.id);
            if (existingIndex !== -1) {
              // Update existing assignment
              state.assignments[existingIndex] = newAssignment;
            } else {
              // Add new assignment
              state.assignments.push(newAssignment);
            }
          });
        }
      })
      .addCase(fetchDressAssignments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch project assignments
      .addCase(fetchProjectAssignments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProjectAssignments.fulfilled, (state, action) => {
        state.isLoading = false;
        // Don't overwrite all assignments, this endpoint is for specific projects
        // Just update or append the new assignments to the existing array
        const newAssignments = action.payload.data;
        if (newAssignments && Array.isArray(newAssignments)) {
          // For each new assignment, check if it exists in the current state
          newAssignments.forEach(newAssignment => {
            const existingIndex = state.assignments.findIndex(a => a.id === newAssignment.id);
            if (existingIndex !== -1) {
              // Update existing assignment
              state.assignments[existingIndex] = newAssignment;
            } else {
              // Add new assignment
              state.assignments.push(newAssignment);
            }
          });
        }
      })
      .addCase(fetchProjectAssignments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Auto assign employee
      .addCase(autoAssignEmployee.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(autoAssignEmployee.fulfilled, (state, action) => {
        state.isLoading = false;
        // Add the new assignment to our list
        state.assignments.push(action.payload.data);
      })
      .addCase(autoAssignEmployee.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearAssignmentError, clearCurrentAssignment } = assignmentsSlice.actions;
export default assignmentsSlice.reducer; 